/**
 * SSE client for ``POST /v1/playground/chat``.
 *
 * The endpoint emits named SSE event types — ``governance``, ``content``,
 * ``done``, ``error`` — interleaved across the response body. This module
 * frames the byte stream, parses the ``event:`` / ``data:`` pair per
 * frame, and fans events out through a typed callback surface.
 *
 * Error model: every transport-level failure (network, non-200) and
 * in-band ``event: error`` frame routes through ``onError``, so callers
 * have a single recovery point. The returned ``abort()`` cancels the
 * fetch and triggers the stop-mid-stream path in the UI.
 */

import type {
  GovernanceStatus,
  GovernanceStepId,
  MessageRole,
} from "./types";

export interface PlaygroundChatMessage {
  role: MessageRole;
  content: string;
}

export interface PlaygroundChatRequest {
  model: string;
  messages: PlaygroundChatMessage[];
  /** Always streams in the playground; backend ignores ``false``. */
  stream?: boolean;
  temperature?: number | null;
  max_tokens?: number | null;
  /** Sesi 2a — reference a versioned skill; backend resolves + injects
   *  the skill's system prompt before the governance pipeline runs. */
  skill_ref?: { skill_id: string; version: number } | null;
}

export interface GovernanceFrame {
  step: GovernanceStepId;
  status: GovernanceStatus;
  ms: number;
  meta: string;
  detail?: Record<string, unknown> | null;
}

export interface ContentChoiceDelta {
  role?: string;
  content?: string;
}

export interface ContentChoice {
  index: number;
  delta: ContentChoiceDelta;
  finish_reason: string | null;
}

export interface ContentFrame {
  id?: string;
  object?: string;
  created?: number;
  model?: string;
  choices: ContentChoice[];
}

export interface DoneFrame {
  usage: {
    chunks_emitted?: number;
    finish_reason?: string;
    tokens?: number;
    cost_usd?: number;
    cost_idr?: number;
  };
  audit_id: string | null;
  session_id: string | null;
  total_ms: number;
}

export interface ErrorFrame {
  type: string;
  code: string;
  message: string;
}

export interface PlaygroundChatHandlers {
  onGovernance?(frame: GovernanceFrame): void;
  onContent?(frame: ContentFrame): void;
  onDone?(frame: DoneFrame): void;
  onError?(error: ErrorFrame): void;
}

export interface PlaygroundChatOptions extends PlaygroundChatHandlers {
  /** Request body. */
  request: PlaygroundChatRequest;
  /** Value sent in ``X-Monago-Session-Id``. */
  sessionId: string;
  /** Bearer token; if omitted the client reads the auth cookie. */
  token?: string;
  /** Optional override for the base URL — defaults to the Vite proxy. */
  baseUrl?: string;
  /** Allows callers to abort the stream from outside (e.g. composer Stop). */
  signal?: AbortSignal;
}

export interface PlaygroundChatHandle {
  /** Resolves once the stream drains (success, error, or abort). */
  done: Promise<void>;
  /** Aborts the underlying fetch; idempotent. */
  abort(reason?: string): void;
}

const ENDPOINT = "/v1/playground/chat";

import Cookies from "js-cookie";
import { API_URL } from "@/api/client";
import { COOKIE_KEYS } from "@/lib/constants";

function resolveToken(explicit?: string): string | undefined {
  if (explicit) return explicit;
  try {
    return Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
  } catch {
    return undefined;
  }
}

/**
 * Open the playground SSE stream. Returns immediately with a handle
 * carrying the drain promise + an abort entry-point. Callers wire the
 * four handlers (``onGovernance`` / ``onContent`` / ``onDone`` /
 * ``onError``) to fan events into the React state layer.
 */
export function streamPlaygroundChat(
  opts: PlaygroundChatOptions
): PlaygroundChatHandle {
  const controller = new AbortController();
  const externalSignal = opts.signal;
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener("abort", () => controller.abort());
  }

  const token = resolveToken(opts.token);
  // ``API_URL`` is ``""`` in dev (Vite proxy handles the rewrite) and
  // the gateway origin in staging / production. Pre-fix this fell back
  // to ``""`` in prod too, resolving to ``console.monago.io/v1/...``
  // (the static-asset origin) which nginx answers with 405. ``baseUrl``
  // remains an explicit override for tests + custom callers.
  const url = `${opts.baseUrl ?? API_URL}${ENDPOINT}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Monago-Session-Id": opts.sessionId,
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const done = (async () => {
    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ stream: true, ...opts.request }),
        signal: controller.signal,
      });
    } catch (err) {
      if (!controller.signal.aborted) {
        opts.onError?.({
          type: "transport_error",
          code: "fetch_failed",
          message:
            err instanceof Error ? err.message : "Network request failed.",
        });
      }
      return;
    }

    if (!response.ok || !response.body) {
      // Parse the JSON envelope first — the gateway returns
      // ``{error: CODE, message: "..."}`` for 4xx pre-flight failures
      // (NO_ACTIVE_CREDENTIAL, UNKNOWN_MODEL, PROVIDER_NOT_CONFIGURED).
      // Surfacing the structured code lets the UI render targeted
      // empty states instead of a generic HTTP error.
      let code = String(response.status);
      let message = `Request failed: HTTP ${response.status}`;
      const contentType = response.headers.get("content-type") ?? "";
      try {
        if (contentType.includes("application/json")) {
          const body = (await response.json()) as {
            error?: string;
            message?: string;
          };
          if (body && typeof body === "object") {
            if (body.error) code = body.error;
            if (body.message) message = body.message;
          }
        } else {
          const text = await response.text();
          if (text) message = text;
        }
      } catch {
        /* swallow */
      }
      opts.onError?.({
        type: "http_error",
        code,
        message,
      });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    try {
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        let separatorIdx = buffer.indexOf("\n\n");
        while (separatorIdx !== -1) {
          const rawFrame = buffer.slice(0, separatorIdx);
          buffer = buffer.slice(separatorIdx + 2);
          dispatchFrame(rawFrame, opts);
          separatorIdx = buffer.indexOf("\n\n");
        }
      }
      // Flush any trailing frame (rare — backend always closes with \n\n).
      const tail = buffer.trim();
      if (tail.length > 0) dispatchFrame(tail, opts);
    } catch (err) {
      if (controller.signal.aborted) return;
      opts.onError?.({
        type: "transport_error",
        code: "stream_failed",
        message:
          err instanceof Error ? err.message : "Stream interrupted.",
      });
    }
  })();

  return {
    done,
    abort(reason?: string): void {
      if (controller.signal.aborted) return;
      controller.abort(reason);
    },
  };
}

/**
 * Parse one SSE frame and dispatch it to the matching handler.
 * Frames missing an ``event:`` line are ignored — the playground server
 * never emits unnamed frames.
 */
export function dispatchFrame(
  rawFrame: string,
  handlers: PlaygroundChatHandlers
): void {
  const { event, data } = parseFrame(rawFrame);
  if (!event) return;
  switch (event) {
    case "governance":
      if (data) handlers.onGovernance?.(data as GovernanceFrame);
      break;
    case "content":
      if (data) handlers.onContent?.(data as ContentFrame);
      break;
    case "done":
      if (data) handlers.onDone?.(data as DoneFrame);
      break;
    case "error":
      if (data && typeof data === "object" && "error" in (data as object)) {
        handlers.onError?.(
          (data as { error: ErrorFrame }).error
        );
      } else if (data) {
        handlers.onError?.(data as ErrorFrame);
      }
      break;
    default:
      // Unknown event — silently drop so a future backend addition is
      // backward-compatible on the wire.
      break;
  }
}

/**
 * Split one frame into ``event`` + parsed ``data`` JSON.
 *
 * Tolerates:
 * * multiple ``data:`` lines per frame (joined with newlines per spec);
 * * leading colon comment lines (heartbeats);
 * * arbitrary whitespace after the colon delimiter.
 */
export function parseFrame(
  rawFrame: string
): { event: string | null; data: unknown } {
  let event: string | null = null;
  const dataLines: string[] = [];

  for (const line of rawFrame.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (dataLines.length === 0) {
    return { event, data: null };
  }
  const payload = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(payload) };
  } catch {
    return { event, data: payload };
  }
}
